import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, Filter, X } from "lucide-react";
import { toast } from "react-toastify";
import dashboardRequests from "../../api/DashboardRequests";

const pad = (number) => String(number).padStart(2, "0");
const toIsoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const getMonthToDateRange = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: toIsoDate(firstOfMonth), to: toIsoDate(today) };
};

const formatRangeLabel = (fromIsoDate, toIsoDateString) => {
  const formatOptions = { day: "numeric", month: "short", year: "numeric" };
  const fromLabel = new Date(`${fromIsoDate}T00:00:00`).toLocaleDateString("en-GB", formatOptions);
  const toLabel = new Date(`${toIsoDateString}T00:00:00`).toLocaleDateString("en-GB", formatOptions);
  return `${fromLabel} – ${toLabel}`;
};

// A 5th stats card that, unlike the other 4 (generic, bulk-fetched by
// StatsGrid from admin_stats), owns its own independent fetch against
// GET admin/sms-usage/count?from=&to= and lets the admin re-query a custom
// date range via a filter icon that reveals an inline picker. Defaults to
// month-to-date (first of this month through today) on first load.
const SmsUsageCard = ({ cardVariants }) => {
  const defaultRange = getMonthToDateRange();
  const [appliedFrom, setAppliedFrom] = useState(defaultRange.from);
  const [appliedTo, setAppliedTo] = useState(defaultRange.to);
  const [draftFrom, setDraftFrom] = useState(defaultRange.from);
  const [draftTo, setDraftTo] = useState(defaultRange.to);
  const [totalSmsSent, setTotalSmsSent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const fetchCount = useCallback(async (from, to) => {
    setIsLoading(true);
    try {
      const response = await dashboardRequests.getSmsUsageCount(from, to);
      if (response.success && response.data?.status_code === "000") {
        setTotalSmsSent(response.data.data.total_sms_sent);
      } else {
        toast.error(response.data?.reason || response.error || "Failed to fetch SMS usage count");
      }
    } catch (error) {
      toast.error("Failed to fetch SMS usage count");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount(appliedFrom, appliedTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleFilter = () => {
    if (!isFilterOpen) {
      setDraftFrom(appliedFrom);
      setDraftTo(appliedTo);
    }
    setIsFilterOpen((currentlyOpen) => !currentlyOpen);
  };

  const handleApply = () => {
    if (draftFrom > draftTo) {
      toast.error("\"From\" date must be before \"To\" date");
      return;
    }
    setAppliedFrom(draftFrom);
    setAppliedTo(draftTo);
    setIsFilterOpen(false);
    fetchCount(draftFrom, draftTo);
  };

  const handleResetToThisMonth = () => {
    const monthToDate = getMonthToDateRange();
    setAppliedFrom(monthToDate.from);
    setAppliedTo(monthToDate.to);
    setDraftFrom(monthToDate.from);
    setDraftTo(monthToDate.to);
    setIsFilterOpen(false);
    fetchCount(monthToDate.from, monthToDate.to);
  };

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ scale: 1.02, y: -5, transition: { duration: 0.2 } }}
      className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-md"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <motion.div
            className="p-2 bg-indigo-100 rounded-lg"
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <BarChart3 className="w-6 h-6 text-indigo-600" />
          </motion.div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">SMS Usage</p>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? "…" : (totalSmsSent ?? 0).toLocaleString()}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleToggleFilter}
          className={`p-1.5 rounded-lg transition-colors ${isFilterOpen ? "text-indigo-600 bg-indigo-50" : "text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"}`}
          title="Filter by date range"
        >
          {isFilterOpen ? <X className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
        </button>
      </div>

      <p className="mt-2 text-xs text-gray-500">{formatRangeLabel(appliedFrom, appliedTo)}</p>

      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 mt-3 space-y-2 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block mb-1 text-[10px] font-medium tracking-wide text-gray-500 uppercase">From</label>
                  <input
                    type="date"
                    value={draftFrom}
                    max={draftTo}
                    onChange={(event) => setDraftFrom(event.target.value)}
                    className="px-2 py-1.5 w-full text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-[10px] font-medium tracking-wide text-gray-500 uppercase">To</label>
                  <input
                    type="date"
                    value={draftTo}
                    min={draftFrom}
                    onChange={(event) => setDraftTo(event.target.value)}
                    className="px-2 py-1.5 w-full text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleApply}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={handleResetToThisMonth}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                >
                  This Month
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SmsUsageCard;
